import { Accordion } from '@any-ui-react/core';
import { memo, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router';

import { MaterialIcons } from '@/common/components/shared/material-icons';
import { useCurrentEmployeeContext } from '@/common/contexts/CurrentEmployeeContext';
import { useIdpAccess } from '@/common/hooks/idp';
import { useScreenBreakpoint } from '@/common/hooks/shared';

import { NavigationLink } from './NavigationLink';

const allMenus = ['dashboard', 'employees', 'probation-review', 'performance-review', 'report', 'setting', 'help'];

export const NavigationLinks = memo(({ onLinkClick }: { onLinkClick?: () => void }) => {
  const location = useLocation();
  const isLargeScreen = useScreenBreakpoint('lg');
  const { currentEmployee } = useCurrentEmployeeContext();
  const { isAdminOrAbove } = useIdpAccess();
  const {
    hasEmployeeAddAccess,
    hasEmployeeAlertListViewAccess,
    hasEmployeeExternalAccountListViewAccess,
    hasPendingChangesViewAccess,
    hasTeamNamesAccess,
    hasPerformanceReviewResultsListAccess,
    hasPerformanceReviewStageAccess,
    hasProbationReviewManageAccess,
    hasReportViewAccess,
    hasSettingViewAccess,
    hasNotificationViewAccess,
    isSuperAdmin,
  } = useMemo(
    () => ({
      hasEmployeeAddAccess: currentEmployee?.accessFlag?.employeeAdd,
      hasEmployeeAlertListViewAccess: currentEmployee?.accessFlag?.employeeAlertListView,
      hasEmployeeExternalAccountListViewAccess: currentEmployee?.accessFlag?.employeeExternalAccountListView,
      hasPendingChangesViewAccess: currentEmployee?.accessFlag?.employeePendingChangesListView,
      // 2026-09-15 (research.md #36, FR-019): widened from SUPER_ADMIN-only to also admit
      // ADMIN_PRIVATE, mirroring router.tsx's own widened route gate.
      hasTeamNamesAccess: currentEmployee?.accessFlag?.isSuperAdmin || currentEmployee?.accessFlag?.isAdminPrivate,
      hasPerformanceReviewResultsListAccess: currentEmployee?.accessFlag?.performanceReviewResultsList,
      hasPerformanceReviewStageAccess: currentEmployee?.accessFlag?.performanceReviewStage,
      hasProbationReviewManageAccess: currentEmployee?.accessFlag?.probationReviewManage,
      hasReportViewAccess: currentEmployee?.accessFlag?.reportView,
      hasSettingViewAccess: currentEmployee?.accessFlag?.settingView,
      hasNotificationViewAccess: currentEmployee?.accessFlag?.settingNotificationView,
      isSuperAdmin: currentEmployee?.accessFlag?.isSuperAdmin,
    }),
    [
      currentEmployee?.accessFlag?.employeeAdd,
      currentEmployee?.accessFlag?.employeeAlertListView,
      currentEmployee?.accessFlag?.employeeExternalAccountListView,
      currentEmployee?.accessFlag?.employeePendingChangesListView,
      currentEmployee?.accessFlag?.isSuperAdmin,
      currentEmployee?.accessFlag?.isAdminPrivate,
      currentEmployee?.accessFlag?.performanceReviewResultsList,
      currentEmployee?.accessFlag?.performanceReviewStage,
      currentEmployee?.accessFlag?.probationReviewManage,
      currentEmployee?.accessFlag?.reportView,
      currentEmployee?.accessFlag?.settingView,
      currentEmployee?.accessFlag?.settingNotificationView,
    ],
  );
  const defaultActiveItem = useMemo(() => {
    if (isLargeScreen) {
      return [location.pathname.split('/')[1]];
    } else {
      return allMenus;
    }
  }, [location.pathname, isLargeScreen]);
  const [openedItem, setOpenedItem] = useState<string[]>(defaultActiveItem);

  useEffect(() => {
    if (isLargeScreen) {
      setOpenedItem([location.pathname.split('/')[1]]);
    } else {
      setOpenedItem(allMenus);
    }
  }, [isLargeScreen, location.pathname]);

  return (
    <div className="my-2 px-2">
      <Accordion<true>
        chevron={null}
        classNames={{
          item: 'border-none',
          control: 'p-0 text-xs font-semibold my-0.5',
          label: 'p-0 text-sm font-semibold',
          content: 'p-0',
          chevron: 'hidden',
        }}
        defaultValue={defaultActiveItem}
        multiple={true}
        value={openedItem}
        onChange={value => {
          if (value !== null && isLargeScreen) {
            setOpenedItem(typeof value === 'string' ? [value] : value);
          }
        }}
      >
        <Accordion.Item value="dashboard">
          <Accordion.Control>
            <NavigationLink
              href="/dashboard"
              icon={<MaterialIcons iconName="home" fill={true} className="text-gray-7" fontSize="24px" />}
              name="Dashboard"
              onLinkClick={onLinkClick}
            />
          </Accordion.Control>
        </Accordion.Item>
        <Accordion.Item value="employees">
          <Accordion.Control>
            <NavigationLink
              control={true}
              href="/employees"
              icon={<MaterialIcons iconName="groups" fill={true} className="text-gray-7" fontSize="24px" />}
              name="Employees"
              onLinkClick={onLinkClick}
            />
          </Accordion.Control>

          <Accordion.Panel className="flex flex-col">
            <NavigationLink href="/employees/list" name="Employee List" subMenu={true} onLinkClick={onLinkClick} />

            <NavigationLink
              href="/employees/add"
              name="Add Employee"
              subMenu={true}
              visible={hasEmployeeAddAccess}
              onLinkClick={onLinkClick}
            />

            <NavigationLink
              href="/employees/organization-chart"
              name="Organization Chart"
              subMenu={true}
              onLinkClick={onLinkClick}
            />
            <NavigationLink
              href="/employees/alert"
              name="Alerts"
              subMenu={true}
              visible={hasEmployeeAlertListViewAccess}
              onLinkClick={onLinkClick}
            />
            <NavigationLink
              href="/employees/pending-changes"
              name="Pending Changes"
              subMenu={true}
              visible={hasPendingChangesViewAccess}
              onLinkClick={onLinkClick}
            />
            <NavigationLink
              href="/employees/external-accounts"
              name="External Accounts"
              subMenu={true}
              visible={hasEmployeeExternalAccountListViewAccess}
              onLinkClick={onLinkClick}
            />
            <NavigationLink
              href="/employees/team-names"
              name="Team Names"
              subMenu={true}
              visible={hasTeamNamesAccess}
              onLinkClick={onLinkClick}
            />
          </Accordion.Panel>
        </Accordion.Item>

        <Accordion.Item value="performance-review">
          <Accordion.Control>
            <NavigationLink
              control={true}
              href="/performance-review"
              icon={<MaterialIcons iconName="star" fill={true} className="text-gray-7" fontSize="24px" />}
              name="Performance Review"
              onLinkClick={onLinkClick}
            />
          </Accordion.Control>

          <Accordion.Panel className="flex flex-col">
            <NavigationLink
              href="/performance-review/list"
              name="Performance Review List"
              subMenu={true}
              onLinkClick={onLinkClick}
            />

            <NavigationLink
              href="/performance-review/results"
              name="Results List"
              subMenu={true}
              visible={hasPerformanceReviewResultsListAccess}
              onLinkClick={onLinkClick}
            />

            <NavigationLink
              href="/performance-review/idp/list"
              name="IDP List"
              subMenu={true}
              visible={isAdminOrAbove}
              onLinkClick={onLinkClick}
            />
          </Accordion.Panel>
        </Accordion.Item>

        <Accordion.Item value="probation-review">
          <Accordion.Control>
            <NavigationLink
              control={true}
              href="/probation-review"
              icon={<MaterialIcons iconName="control_camera" fill={true} className="text-gray-7" fontSize="24px" />}
              name="Probation Review"
              onLinkClick={onLinkClick}
            />
          </Accordion.Control>

          <Accordion.Panel className="flex flex-col">
            <NavigationLink
              href="/probation-review/list"
              name="Probation Review List"
              subMenu={true}
              onLinkClick={onLinkClick}
            />
            <NavigationLink
              href="/probation-review/audit-log"
              name="Audit Log"
              subMenu={true}
              visible={hasProbationReviewManageAccess}
              onLinkClick={onLinkClick}
            />
          </Accordion.Panel>
        </Accordion.Item>

        {hasReportViewAccess && (
          <Accordion.Item value="report">
            <Accordion.Control>
              <NavigationLink
                control={true}
                href="/report/number-of-employees"
                icon={<MaterialIcons iconName="analytics" fill={true} className="text-gray-7" fontSize="24px" />}
                name="Report"
                onLinkClick={onLinkClick}
              />
            </Accordion.Control>
            <Accordion.Panel className="flex flex-col">
              <NavigationLink
                href="/report/number-of-employees"
                name="Number of Employees"
                subMenu={true}
                onLinkClick={onLinkClick}
              />
              <NavigationLink
                href="/report/performance-review"
                name="Performance Review"
                subMenu={true}
                onLinkClick={onLinkClick}
              />
              <NavigationLink
                href="/report/managerial-accounting"
                name="Managerial Accounting"
                subMenu={true}
                onLinkClick={onLinkClick}
              />
              <NavigationLink
                href="/report/demographic-report"
                name="Demographic Report"
                subMenu={true}
                onLinkClick={onLinkClick}
              />
            </Accordion.Panel>
          </Accordion.Item>
        )}

        {hasSettingViewAccess && (
          <Accordion.Item value="setting">
            <Accordion.Control>
              <NavigationLink
                control={true}
                href="/setting"
                icon={<MaterialIcons iconName="settings" fill={true} className="text-gray-7" fontSize="24px" />}
                name="Setting"
                onLinkClick={onLinkClick}
              />
            </Accordion.Control>
            <Accordion.Panel className="flex flex-col">
              <NavigationLink
                href="/setting/performance-review"
                name="Performance Review"
                subMenu={true}
                visible={hasPerformanceReviewStageAccess || isSuperAdmin}
                onLinkClick={onLinkClick}
              />
              <NavigationLink
                href="/setting/notification"
                name="Notification"
                subMenu={true}
                visible={hasNotificationViewAccess}
                onLinkClick={onLinkClick}
              />
              <NavigationLink href="/setting/general" name="General" subMenu={true} onLinkClick={onLinkClick} />
              <NavigationLink
                href="/setting/org-hierarchy-relation"
                name="Relation Management"
                subMenu={true}
                onLinkClick={onLinkClick}
              />
              <NavigationLink href="/setting/ai/mcp" name="MCP Connections" subMenu={true} onLinkClick={onLinkClick} />
              <NavigationLink
                href="/setting/ai/assistant"
                name="AI Assistant"
                subMenu={true}
                visible={currentEmployee?.accessFlag.isSuperAdmin}
                onLinkClick={onLinkClick}
              />
            </Accordion.Panel>
          </Accordion.Item>
        )}

        <Accordion.Item value="help">
          <Accordion.Control>
            <NavigationLink
              href="https://sites.google.com/adasiaholdings.com/hrallabouts/home"
              icon={
                <MaterialIcons iconName="help" fill={true} className="text-gray-7 cursor-pointer" fontSize="24px" />
              }
              rightIcon={
                <MaterialIcons iconName="open_in_new" className="text-gray-7 cursor-pointer" fontSize="16px" />
              }
              name="Help"
              target="_blank"
              onLinkClick={onLinkClick}
            />
          </Accordion.Control>
        </Accordion.Item>
      </Accordion>
    </div>
  );
});
